import React from 'react';
import { useOptionalFilterState } from '@/hooks/useFilter';
import { useAuthState } from '@/utils/auth';
import { useTheme } from './theme-provider';

export const ChatwootWidget = () => {
  const { user } = useAuthState();
  const filter = useOptionalFilterState();
  const { theme } = useTheme();
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    // Add Chatwoot Settings
    (window as any).chatwootSettings = {
      useBrowserLanguage: true,
      darkMode: theme === 'light' ? 'light' : 'auto',
      hideMessageBubble: true,
      position: 'right',
    };
  }, [theme]);

  React.useEffect(() => {
    const handler = () => {
      setLoaded(true);
    };
    window.addEventListener('chatwoot:ready', handler);
    // `chatwoot:ready` fires once per page load. If it fired while no instance
    // was listening (mobile sidebar sheet closed, layout remount), read the
    // SDK flag instead or setUser never runs for this instance.
    if ((window as any).$chatwoot?.hasLoaded) {
      setLoaded(true);
    }
    return () => {
      window.removeEventListener('chatwoot:ready', handler);
    };
  }, []);

  React.useEffect(() => {
    // Paste the script from inbox settings except the <script> tag
    (function (d, t) {
      var BASE_URL = 'https://app.chatwoot.com';
      // Guard against injecting the SDK script more than once if this
      // component is mounted multiple times (e.g. multiple triggers/remounts).
      if (d.getElementById('chatwoot-sdk')) {
        return;
      }
      var g = d.createElement(t) as HTMLScriptElement,
        s = d.getElementsByTagName(t)[0] as HTMLScriptElement;
      g.id = 'chatwoot-sdk';
      g.src = BASE_URL + '/packs/js/sdk.js';
      s.parentNode?.insertBefore(g, s);
      g.async = true;
      g.onload = function () {
        (window as any).chatwootSDK?.run({
          websiteToken: 'UvY6tHkCNmPcS7BDYR16Se9X',
          baseUrl: BASE_URL,
        });
      };
    })(document, 'script');
  }, []);

  React.useEffect(() => {
    (async () => {
      if (loaded && user) {
        (window as any).$chatwoot?.setUser(user.id, {
          name: `${user.first_name} ${user.last_name}`,
          identifier_hash: user.identifier_hash,
          email: user.email,
          phone_number: user.phone,
          company_name: filter.names.company,
        });
      }
    })();
  }, [loaded, user, filter.names.company]);

  return null;
};

export const ChatwootWidgetTrigger: React.FC<
  Omit<React.HTMLProps<HTMLButtonElement>, 'onClick' | 'type'>
> = (props) => {
  return (
    <>
      <ChatwootWidget />
      <button
        type="button"
        onClick={async () => {
          (window as any).$chatwoot?.toggle('open');
        }}
        {...props}
      />
    </>
  );
};
